// Theme helpers — dark (default) / light toggle persisted in localStorage.
// The attribute is applied to <body> so CSS variables in :root[data-theme="light"]
// (see App.css) take effect app-wide.

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'jvista-theme-v1';

export function getStoredTheme() {
  try { return localStorage.getItem(STORAGE_KEY) || 'dark'; }
  catch { return 'dark'; }
}

export function setStoredTheme(theme) {
  try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
}

export function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  document.body.setAttribute('data-theme', theme);
  // Also mirror on <html> so SVG charts inherit background properly.
  document.documentElement.setAttribute('data-theme', theme);
}

/**
 * React hook for components that need *reactive* theme values — SVG
 * attributes on Recharts (stroke, fill) can't be expressed as CSS vars,
 * so panels call this to get { isDark, theme, colors } and re-render on
 * toggle via a MutationObserver on <body data-theme>.
 *
 * colors maps the common Recharts attributes to theme-aware hex so
 * callers don't need to branch on isDark everywhere:
 *   plotBg, plotText, plotGrid, axis, tick, accent, green, amber, red.
 */
const DARK_COLORS = {
  plotBg:   '#0b1016',
  plotText: '#8b949e',
  plotGrid: '#2a3f55',
  axis:     '#8b949e',
  tick:     '#8b949e',
  accent:   '#00d4ff',
  green:    '#39d353',
  amber:    '#f0a500',
  red:      '#ff4757',
  text:     '#e6edf3',
  textDim:  '#8b949e',
  panelBg:  '#131920',
  border:   '#30363d',
};
const LIGHT_COLORS = {
  plotBg:   '#ffffff',
  plotText: '#4a4a6a',
  plotGrid: '#e9ecef',
  axis:     '#4a4a6a',
  tick:     '#4a4a6a',
  accent:   '#0052cc',
  green:    '#28a745',
  amber:    '#d97706',
  red:      '#dc3545',
  text:     '#1a1a2e',
  textDim:  '#6c757d',
  panelBg:  '#ffffff',
  border:   '#dee2e6',
};

/**
 * Shared palette for panels that pass theme as a prop.  Returns a flat
 * object with every colour a panel commonly reaches for so each file
 * can just `const c = themeColors(theme);` instead of re-declaring a
 * big ternary block.
 */
export function themeColors(theme) {
  const isDark = theme !== 'light';
  return isDark ? {
    isDark: true,
    bg:               '#0d1117',
    bgSecondary:      '#161b22',
    bgCard:           '#131920',
    text:             '#e6edf3',
    textMuted:        '#8b949e',
    textDim:          '#6a8299',
    border:           '#30363d',
    accent:           '#00d4ff',
    accentDark:       '#00a8cc',
    accentGlow:       'rgba(0,212,255,0.12)',
    green:            '#39d353',
    amber:            '#f0a500',
    red:              '#ff4757',
    input:            '#21262d',
    inputText:        '#e6edf3',
    plotBg:           '#0d1117',
    plotText:         '#8b949e',
    plotGrid:         '#21262d',
    btnPrimary:       '#00d4ff',
    btnPrimaryText:   '#000000',
    btnSecondary:     '#21262d',
    btnSecondaryText: '#e6edf3',
    btnActive:        '#00d4ff',
    btnInactive:      '#30363d',
    btnInactiveText:  '#8b949e',
    aiUserBg:         '#1f6feb',
  } : {
    isDark: false,
    bg:               '#ffffff',
    bgSecondary:      '#f8f9fa',
    bgCard:           '#ffffff',
    text:             '#1a1a2e',
    textMuted:        '#6c757d',
    textDim:          '#6c757d',
    border:           '#dee2e6',
    accent:           '#0052cc',
    accentDark:       '#003d99',
    accentGlow:       'rgba(0,82,204,0.08)',
    green:            '#28a745',
    amber:            '#d97706',
    red:              '#dc3545',
    input:            '#ffffff',
    inputText:        '#1a1a2e',
    plotBg:           '#ffffff',
    plotText:         '#4a4a6a',
    plotGrid:         '#e9ecef',
    btnPrimary:       '#0052cc',
    btnPrimaryText:   '#ffffff',
    btnSecondary:     '#e9ecef',
    btnSecondaryText: '#1a1a2e',
    btnActive:        '#0052cc',
    btnInactive:      '#adb5bd',
    btnInactiveText:  '#6c757d',
    aiUserBg:         '#0052cc',
  };
}

export function useTheme() {
  const [theme, setTheme] = useState(() =>
    (typeof document !== 'undefined' &&
      document.body.getAttribute('data-theme')) || 'dark');
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const obs = new MutationObserver(() => {
      setTheme(document.body.getAttribute('data-theme') || 'dark');
    });
    obs.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  const isDark = theme !== 'light';
  const colors = isDark ? DARK_COLORS : LIGHT_COLORS;
  return { theme, isDark, colors };
}
